/**
 * Admin > Multi-factor DEMA-WMA Gainer (TEK_DEMA_WMA_GAINER): configuration,
 * dashboard and controls. Live trading is enabled only via `setLiveApproval`
 * (typed phrase + caps) and still needs the platform live switches.
 */
import type { BacktestReferenceData } from "@/types";
import api from "@/services/api";

/** The original strategy's API. */
export const DEMA_WMA_BASE = "/admin/strategies/dema-wma-gainer";
/** The independent, PAPER-ONLY Enhanced DEMA-WMA Gainer v33 (TEK_DEMA_WMA_GAINER_V33_ENH): same endpoints. */
export const ENHANCED_DEMA_WMA_BASE = "/admin/strategies/dema-wma-gainer-enhanced";

export type DemaWmaFieldValue = number | string | boolean | string[] | null;

export interface DemaWmaOverview {
  algo_id: string;
  algo_name: string;
  strategy_key: string;
  /** Enhanced strategy only: the strategy / version it was forked from. */
  parent?: { strategy_key: string; version: number } | null;
  /** True = never live, never customer-activatable (the enhanced strategy). */
  paper_only?: boolean;
  /** The admin instance's PAPER activity today (IST). */
  today?: { date: string; entries: number; max_trades_per_day: number | null; open_positions: number; closed_net_pnl: number };
  execution_status: string;
  entries_paused: boolean;
  runner_available: boolean;
  active_version: number | null;
  config_hash: string;
  config: Record<string, unknown>;
  fields: Record<string, DemaWmaFieldValue>;
  validation_errors: string[];
  suggested_values: { field: string; label: string; value: unknown }[];
  suggested_fields: string[];
  live_trading: DemaWmaLiveTrading;
  /** The active version carries its own valid live approval. */
  live_approved?: boolean;
  instruments_info: { symbol_code: string; lot_size: number; strike_step: number }[];
  rule_set?: string;
  versions: { version: number; is_active: boolean; rule_set?: string; timeframe_minutes?: number; notes: string | null; created_at: string | null; activated_at: string | null }[];
}

export interface DemaWmaLiveTrading {
  enabled: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  approved_version?: number;
  instruments?: string[];
  max_lots?: number;
  max_daily_loss?: number | null;
}

export interface DemaWmaLiveStatus {
  live_orders_will_be_sent: boolean;
  gates: Record<string, boolean>;
  entries_paused: boolean;
  broker: string | null;
  active_version: number | null;
  live_trading: DemaWmaLiveTrading;
  open_live_positions: number;
  confirmation_phrase: string;
}

export interface DemaWmaLiveApprovalRequest {
  enable: boolean;
  confirmation?: string;
  instruments?: string[];
  max_lots?: number;
  max_daily_loss?: number;
}

export const activationConfirmation = (version: number): string => `ACTIVATE DEMA-WMA VERSION ${version}`;

export interface DemaWmaSaveResult {
  version: number;
  config_hash: string;
  changed: Record<string, { from: unknown; to: unknown }>;
}

export interface DemaWmaPositionView {
  id: string;
  mode: string;
  index: string;
  /** "PLATFORM" or the customer's e-mail (only on the all-owner views). */
  owner?: string;
  user_id?: string | null;
  option_type: string;
  strike: number | null;
  expiry_date: string | null;
  quantity: number;
  entry_price: number | null;
  entry_time: string | null;
  stop_loss: number | null;
  target: number | null;
  status: string;
  exit_price: number | null;
  exit_time: string | null;
  exit_reason: string | null;
  gross_pnl: number | null;
  charges: number | null;
  net_pnl: number | null;
  last_mark: number | null;
  last_mark_at?: string | null;
  /** Latched exit still waiting for a fill (exit signal fired, no usable quote yet). */
  pending_exit?: { reason: string | null; attempts: number | null; last_error: string | null; latched_at: string | null; last_attempt_at: string | null } | null;
  /** Exit filled at a stored fallback price because no live quote was usable. */
  exit_fallback?: { source: string; age_seconds: number; live_reject: string; attempt: number } | null;
  manual_reconciliation?: boolean;
  unrealized_pnl: number | null;
  kill_requested: boolean;
}

export interface DemaWmaSignalView {
  bar_start: string | null;
  index: string;
  signal_type: string;
  side: string | null;
  decision: string | null;
  reason: string | null;
  suppressed_by: string | null;
  values: Record<string, number | boolean | null> | null;
  /** Config version that evaluated the candle. */
  config_version?: number | null;
  /** When the signal row was written (latency: compare with bar_start + 1 minute). */
  written_at?: string | null;
  /** Entry-condition checklist per side (null when the candle could not be evaluated). */
  checks?: DemaWmaSignalChecks | null;
}

export interface DemaWmaConditionCheck {
  name: string;
  passed: boolean;
  value: number | null;
  rule: string;
}

export interface DemaWmaSignalChecks {
  CE: DemaWmaConditionCheck[];
  PE: DemaWmaConditionCheck[];
  CE_all: boolean;
  PE_all: boolean;
}

export interface DemaWmaEntryBlock {
  owner: string;
  user_id: string | null;
  index: string | null;
  reason: string;
  code: string;
  at: string | null;
}

export interface DemaWmaOrderView {
  created_at: string | null;
  intent: string;
  side: string;
  state: string;
  option_type: string | null;
  strike: number | null;
  quantity: number;
  filled_quantity: number;
  intended_price: number | null;
  limit_price: number | null;
  avg_fill_price: number | null;
  reason: string | null;
}

export interface DemaWmaCustomerActivation {
  user_id: string;
  email: string;
  status: string;
  error_message: string | null;
  broker: string | null;
  activated_at: string | null;
  open_positions: DemaWmaPositionView[];
  today: { entries: number; closed: number; net_pnl: number };
}

export interface DemaWmaDashboard {
  mode: string;
  /** Customer activations (Algo Strategies > Activate), each on its own broker account. */
  customers?: DemaWmaCustomerActivation[];
  /** LIVE mode only: every gate is on, so real broker orders are sent. */
  live_orders_will_be_sent?: boolean;
  execution_status: string;
  entries_paused: boolean;
  today: { entries: number; closed: number; open: number; gross_pnl: number; charges: number; net_pnl: number; unrealized_pnl: number };
  indices: Record<string, { bar_start: string | null; decision: string | null; reason: string | null; suppressed_by: string | null;
    side: string | null; state: string | null; values: Record<string, number | boolean | null> | null }>;
  open_positions: DemaWmaPositionView[];
  trades: DemaWmaPositionView[];
  /** Platform + every customer, PAPER + LIVE (display only). */
  all_open_positions?: DemaWmaPositionView[];
  /** Newest closed trades of the platform and every customer, PAPER + LIVE. */
  trade_book?: DemaWmaPositionView[];
  /** Latest entry block today per owner and index, when no entry followed it. */
  entry_blocks?: DemaWmaEntryBlock[];
  signals: DemaWmaSignalView[];
  orders: DemaWmaOrderView[];
  /** Process-wide Breeze REST call accounting (absent on older backends). */
  breeze?: { calls_today: number; calls_last_60s: number; daily_limit: number | null; limit_exceeded_at: string | null;
    limit_message: string | null; by_call?: Record<string, number> };
}

export const KILL_CONFIRMATION = "STOP DEMA-WMA GAINER";

/** OPTION_1MIN = stored 1-minute option candles where available (else the OPTION_CHAIN prices). */
export type DemaWmaPnlModel = "OPTION_DELTA" | "OPTION_CHAIN" | "OPTION_1MIN";

export interface DemaWmaBacktestRequest {
  from_date: string;
  to_date: string;
  pnl_model: DemaWmaPnlModel;
  option_delta?: number;
  assumed_premium_banknifty?: number;
  assumed_premium_nifty?: number;
  /** Saved version to test (default: the active one). */
  config_version?: number;
  /** What-if sensitivity only; never changes the stored config. */
  stop_loss_pct_override?: number;
  disable_stop_loss?: boolean;
  wma_only_exits?: boolean;
  /** 1-minute configs only: tolerated missing minutes per session (server default 3, max 15). */
  max_missing_minutes_per_session?: number;
  /** 5-minute configs: NATIVE = stored 5-minute bars (server default); FROM_1MIN = built from 1-minute candles. */
  bar_source?: DemaWmaBarSource;
  /** DATED (server default) = expiry, lot size and charge rates as of each trade date; CURRENT = today's values. */
  reference_data?: "DATED" | "CURRENT";
  /** OPTION_CHAIN / OPTION_1MIN: entries fill at the first stored option price at or after the signal, at most this many seconds later (server default 60). */
  chain_max_lag_seconds?: number;
  /** 1-minute index candles: AUTO (server default) = per session the live feed when complete, else Breeze history;
   *  LIVE / BREEZE_HISTORICAL = only that source. Never mixed within a session. */
  signal_source?: DemaWmaSignalSource;
}

export type DemaWmaSignalSource = "AUTO" | "LIVE" | "BREEZE_HISTORICAL";

/** One session of a 1-minute backtest's data report (`data_quality[index].sessions`). */
export interface DemaWmaSessionReport {
  date: string;
  role: "range" | "warm-up";
  trading_day: boolean;
  source?: "LIVE" | "BREEZE" | null;
  bars?: number;
  missing_minutes?: number;
  missing_ranges?: string[];
  gap_flagged_bars?: number;
}

export type DemaWmaBarSource = "NATIVE" | "FROM_1MIN";

export interface DemaWmaBacktestTrade {
  index: string;
  side: string;
  strike: number;
  expiry: string;
  quantity: number;
  lots: number;
  signal_bar_end: string;
  entry_time: string;
  entry_underlying: number;
  entry_premium: number;
  stop_loss: number;
  target: number;
  exit_time: string | null;
  exit_underlying: number | null;
  exit_premium: number | null;
  exit_reason: string | null;
  gross_pnl: number | null;
  charges: number | null;
  net_pnl: number | null;
  bars_held: number;
  /** OPTION_CHAIN evidence (absent on older runs). */
  entry_price_lag_s?: number | null;
  exit_price_lag_s?: number | null;
  entry_price_source?: string | null;
  exit_price_source?: string | null;
  exit_fill_basis?: string | null;
}

export interface DemaWmaLagStats { n: number; median_s: number | null; max_s: number | null; over_60s: number; over_300s: number }

export interface DemaWmaPriceQuality {
  max_entry_lag_seconds: number;
  entries_unpriceable: number;
  entry_lag: DemaWmaLagStats;
  exit_lag_bar_close_exits: DemaWmaLagStats;
  exit_fill_basis: Record<string, number>;
  price_sources: Record<string, number>;
  /** OPTION_1MIN only: fills priced from 1-minute candles vs from the ~5-minute fallback. */
  fills_1min?: number;
  fills_5min_fallback?: number;
}

export interface DemaWmaBucket { trades: number; wins: number; losses: number; win_rate_pct: number | null; net_pnl: number }

export interface DemaWmaBacktestResult {
  backtest_id?: string;
  created_at?: string;
  config_version?: number | null;
  from_date?: string;
  to_date?: string;
  metrics: (Partial<{
    total_trades: number; wins: number; losses: number; win_rate_pct: number; gross_pnl: number; charges: number;
    net_pnl: number; avg_win: number | null; avg_loss: number | null; profit_factor: number | null; max_drawdown: number;
    avg_bars_held: number;
  }> & { by_index?: Record<string, DemaWmaBucket>; by_side?: Record<string, DemaWmaBucket>; by_exit_reason?: Record<string, DemaWmaBucket> }) | null;
  trades: DemaWmaBacktestTrade[];
  rejections: Record<string, number>;
  signals?: Record<string, number>;
  data_quality: Record<string, Record<string, unknown>>;
  settings: Record<string, unknown>;
  assumptions: string;
  note?: string;
  /** Absent on runs stored before the shared loader. */
  bar_source?: DemaWmaBarSource;
  timeframe_minutes?: number;
  reference_data?: BacktestReferenceData;
  /** OPTION_CHAIN only; absent on runs stored before the pricing fixes (2026-10-26). */
  price_quality?: DemaWmaPriceQuality | null;
  /** 1-minute runs; absent on runs stored before the source choice (= AUTO). */
  signal_source?: DemaWmaSignalSource;
}

export interface DemaWmaBacktestRunSummary {
  backtest_id: string;
  created_at: string | null;
  from_date: string;
  to_date: string;
  pnl_model: string;
  trade_count: number;
  config_version: number | null;
}

export type DemaWmaChartSource = "BACKTEST" | "PAPER";
export type DemaWmaChartEventKind = "ENTRY" | "EXIT" | "OPEN" | "REJECTED" | "SKIPPED";
export type DemaWmaExitClass = "STOP_LOSS" | "TARGET" | "WMA_SLOPE" | "EOD" | "MANUAL" | "OTHER";

export interface DemaWmaChartEvent {
  id: string;
  source: DemaWmaChartSource;
  kind: DemaWmaChartEventKind;
  bar_index: number;
  /** Start of the 5-minute bar the event is drawn on (ISO, +05:30). */
  bar_time: string;
  /** Recorded event time (ISO, +05:30). */
  time: string | null;
  side: "CE" | "PE" | null;
  reason: string | null;
  exit_class?: DemaWmaExitClass;
  trade_id?: string;
  position_id?: string;
  strike?: number | null;
  underlying?: number | null;
  premium?: number | null;
  net_pnl?: number | null;
  unrealized_pnl?: number | null;
}

export interface DemaWmaChartTrade {
  source: DemaWmaChartSource;
  trade_id: string;
  position_id: string | null;
  side: "CE" | "PE";
  strike: number | null;
  quantity: number | null;
  status: "OPEN" | "CLOSED";
  entry_time: string | null;
  exit_time: string | null;
  entry_bar: number | null;
  exit_bar: number | null;
  entry_bar_time: string | null;
  exit_bar_time: string | null;
  entry_underlying: number | null;
  exit_underlying: number | null;
  index_points: number | null;
  entry_premium: number | null;
  exit_premium: number | null;
  exit_reason: string | null;
  exit_class: DemaWmaExitClass | null;
  gross_pnl: number | null;
  charges: number | null;
  net_pnl: number | null;
  unrealized_pnl: number | null;
}

export type DemaWmaCompareStatus = "MATCHED" | "EXIT_DIFFERS" | "BACKTEST_ONLY" | "PAPER_ONLY" | "PAPER_OPEN";

export interface DemaWmaCompareSide {
  trade_id: string;
  position_id: string | null;
  status: string;
  entry_time: string | null;
  exit_time: string | null;
  entry_bar: number | null;
  exit_bar: number | null;
  exit_reason: string | null;
  exit_class: DemaWmaExitClass | null;
  entry_premium: number | null;
  exit_premium: number | null;
  index_points: number | null;
  net_pnl: number | null;
  unrealized_pnl: number | null;
}

export interface DemaWmaCompareRow {
  status: DemaWmaCompareStatus;
  side: "CE" | "PE";
  entry_time: string | null;
  entry_bar_gap: number | null;
  backtest: DemaWmaCompareSide | null;
  paper: DemaWmaCompareSide | null;
  net_pnl_diff: number | null;
}

export interface DemaWmaChartSummary {
  closed: number;
  open: number;
  wins: number;
  win_rate_pct: number | null;
  net_pnl: number;
  index_points: number | null;
  by_exit_class: Partial<Record<DemaWmaExitClass, number>>;
}

export interface DemaWmaChart {
  index: string;
  from_date: string;
  to_date: string;
  timeframe_minutes: number;
  config_version: number | null;
  config_hash: string;
  indicator_settings: { dema_fast: number; dema_slow: number; rsi_period: number; rsi_wma: number };
  bars: { t: string; o: number; h: number; l: number; c: number; valid: boolean }[];
  indicators: { dema_fast: (number | null)[]; dema_slow: (number | null)[]; rsi: (number | null)[]; wma: (number | null)[] };
  performance: {
    dema_baseline_points: (number | null)[];
    backtest_points: (number | null)[] | null;
    paper_points: (number | null)[];
  };
  events: DemaWmaChartEvent[];
  trades: DemaWmaChartTrade[];
  unplaced: string[];
  comparison: { rows: DemaWmaCompareRow[]; counts: Partial<Record<DemaWmaCompareStatus, number>>;
    matched_net_pnl_backtest: number; matched_net_pnl_paper: number } | null;
  summary: { BACKTEST: DemaWmaChartSummary | null; PAPER: DemaWmaChartSummary };
  backtest: { backtest_id: string; created_at: string | null; from_date: string; to_date: string; pnl_model: string | null;
    config_version: number | null; has_signal_events: boolean } | null;
  data_quality: Record<string, number | null>;
  notes: string[];
}

export interface DemaWmaChartQuery {
  index: string;
  from_date: string;
  to_date: string;
  backtest_id?: string | null;
}

const chartParams = (q: DemaWmaChartQuery) => ({
  index: q.index, from_date: q.from_date, to_date: q.to_date, ...(q.backtest_id ? { backtest_id: q.backtest_id } : {}),
});

export interface DemaWmaProfitTarget { pct: number; lots: number }

export interface DemaWmaProfitBooking {
  strategy: string;
  strategy_key: string;
  version: number;
  is_active: boolean;
  configured: boolean;
  enabled: boolean;
  live_enabled: boolean;
  total_lots: number;
  targets: DemaWmaProfitTarget[];
  defaults: { total_lots: number; targets: DemaWmaProfitTarget[] };
  remaining_policy: string;
  max_targets: number;
  max_pct: number;
  price_source: string;
  lot_sizes: Record<string, number>;
  updated_at: string | null;
  activated_at: string | null;
  notes: string | null;
  config_hash: string;
  status: string;
}

export interface DemaWmaProfitBookingDraft {
  source_version: number;
  expected_version?: number;
  enabled: boolean;
  total_lots: number;
  targets: DemaWmaProfitTarget[];
  live_enabled: boolean;
  confirm?: string;
}

export const profitBookingLiveConfirmation = (version: number): string =>
  `ENABLE LIVE PROFIT BOOKING FOR DEMA-WMA VERSION ${version}`;

/** One client per DEMA-WMA strategy: `BASE` selects the original or the enhanced strategy's API. */
export function createDemaWmaGainerService(BASE: string) {
  return {
  async getChart(q: DemaWmaChartQuery): Promise<DemaWmaChart> {
    const { data } = await api.get<DemaWmaChart>(`${BASE}/chart`, { params: chartParams(q) });
    return data;
  },

  /** TradingView Pine Script v6 indicator with the same events embedded (plain text). */
  async getPineScript(q: DemaWmaChartQuery): Promise<{ filename: string; script: string }> {
    const response = await api.get<string>(`${BASE}/chart/pine`, { params: chartParams(q), responseType: "text" });
    const cd = String((response.headers as Record<string, unknown>)?.["content-disposition"] ?? "");
    const m = /filename="?([^";]+)"?/.exec(cd);
    return { filename: m ? m[1] : `tekinvest_dema_wma_${q.index.toLowerCase()}.pine`, script: String(response.data) };
  },

  async getDashboard(mode?: "PAPER" | "LIVE"): Promise<DemaWmaDashboard> {
    const { data } = await api.get<DemaWmaDashboard>(`${BASE}/dashboard`, { params: mode ? { mode } : {} });
    return data;
  },

  /** Trade book CSV (platform + every customer, paper and live) for an IST date range; triggers a browser download. */
  async downloadTradesCsv(fromDate: string, toDate: string, mode?: "PAPER" | "LIVE"): Promise<void> {
    const { data, headers } = await api.get<globalThis.Blob>(`${BASE}/trades.csv`, {
      params: { from_date: fromDate, to_date: toDate, ...(mode ? { mode } : {}) },
      responseType: "blob",
    });
    const disposition = String((headers as Record<string, unknown>)["content-disposition"] ?? "");
    const match = /filename="([^"]+)"/.exec(disposition);
    const name = match ? match[1] : `dema_wma_trades_${fromDate}_${toDate}.csv`;
    const g = globalThis;
    const url = g.URL.createObjectURL(data instanceof g.Blob ? data : new g.Blob([String(data)], { type: "text/csv" }));
    const a = g.document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    g.URL.revokeObjectURL(url);
  },

  async getLiveStatus(): Promise<DemaWmaLiveStatus> {
    const { data } = await api.get<DemaWmaLiveStatus>(`${BASE}/live-status`);
    return data;
  },

  /** Approve (typed phrase + caps) or revoke live trading: saves and activates a new version changing only live_trading. */
  async setLiveApproval(payload: DemaWmaLiveApprovalRequest): Promise<{ version: number; previous_version: number; live_trading: DemaWmaLiveTrading }> {
    const { data } = await api.post(`${BASE}/live-approval`, payload);
    return data;
  },

  async setRunning(running: boolean): Promise<{ execution_status: string }> {
    const { data } = await api.post(`${BASE}/running`, { running });
    return data;
  },

  async setPaused(paused: boolean): Promise<{ entries_paused: boolean }> {
    const { data } = await api.post(`${BASE}/entries-paused`, { paused });
    return data;
  },

  async runBacktest(payload: DemaWmaBacktestRequest): Promise<DemaWmaBacktestResult> {
    const { data } = await api.post<DemaWmaBacktestResult>(`${BASE}/backtest`, payload);
    return data;
  },

  async listBacktestRuns(limit = 50): Promise<DemaWmaBacktestRunSummary[]> {
    const { data } = await api.get<DemaWmaBacktestRunSummary[]>(`${BASE}/backtest/runs`, { params: { limit } });
    return data;
  },

  async getBacktestRun(id: string): Promise<DemaWmaBacktestResult> {
    const { data } = await api.get<DemaWmaBacktestResult>(`${BASE}/backtest/${id}`);
    return data;
  },

  async killSwitch(confirm: string): Promise<{ entries_paused: boolean; exits_requested: number }> {
    const { data } = await api.post(`${BASE}/kill-switch`, { confirm });
    return data;
  },

  async getOverview(): Promise<DemaWmaOverview> {
    const { data } = await api.get<DemaWmaOverview>(`${BASE}/overview`);
    return data;
  },

  /** Saves the v2 rule set as a NEW INACTIVE version; never activates it. */
  async saveV2Draft(minDemaGapPoints?: number, notes?: string): Promise<{ version: number; is_active: boolean; rule_set: string; config_hash: string }> {
    const { data } = await api.post(`${BASE}/v2-draft`, {
      ...(minDemaGapPoints ? { min_dema_gap_points: minDemaGapPoints } : {}),
      ...(notes ? { notes } : {}),
    });
    return data;
  },

  /** Explicit activation (also the rollback). Needs the typed confirmation; refused while a position is open. */
  async activateVersion(version: number, confirm: string): Promise<{ active_version: number; previous_version: number | null; rule_set: string }> {
    const { data } = await api.post(`${BASE}/activate-version`, { version, confirm });
    return data;
  },

  async getProfitBooking(version?: number): Promise<DemaWmaProfitBooking> {
    const { data } = await api.get<DemaWmaProfitBooking>(`${BASE}/profit-booking`, { params: version ? { version } : {} });
    return data;
  },

  /** Saves profit booking as a NEW INACTIVE version (copy of source_version); never activates anything. */
  async saveProfitBookingDraft(payload: DemaWmaProfitBookingDraft): Promise<{ version: number; is_active: boolean; changed_fields: string[] }> {
    const { data } = await api.post(`${BASE}/profit-booking-draft`, payload);
    return data;
  },

  async saveConfig(fields: Record<string, DemaWmaFieldValue>, notes?: string): Promise<DemaWmaSaveResult> {
    const { data } = await api.put<DemaWmaSaveResult>(`${BASE}/config`, { ...fields, ...(notes ? { notes } : {}) });
    return data;
  },
};
}

export type DemaWmaGainerServiceApi = ReturnType<typeof createDemaWmaGainerService>;
export const demaWmaGainerService: DemaWmaGainerServiceApi = createDemaWmaGainerService(DEMA_WMA_BASE);
export const enhancedDemaWmaGainerService: DemaWmaGainerServiceApi = createDemaWmaGainerService(ENHANCED_DEMA_WMA_BASE);

export default demaWmaGainerService;
